import React, { useState, useEffect } from 'react';
import { apiService } from '../../../../services/api';
import { Usuario, UpdateUsuarioData, PerfilPermissao } from '../../../../types/admin';

interface ModalEditarUsuarioProps {
  usuario: Usuario;
  onClose: () => void;
  onRefresh: () => void;
}

export const ModalEditarUsuario: React.FC<ModalEditarUsuarioProps> = ({
  usuario,
  onClose,
  onRefresh,
}) => {
  const [formData, setFormData] = useState<UpdateUsuarioData>({
    nome: usuario.nome,
    sobrenome: usuario.sobrenome || '',
    email: usuario.email,
    telefone_celular: usuario.telefone_celular || '',
    data_nascimento: usuario.data_nascimento || '',
    cpf: usuario.cpf || '',
    rg: usuario.rg || '',
    cargo: usuario.cargo || '',
    id_perfil_base: usuario.id_perfil_base,
    foto_perfil_url: usuario.foto_perfil_url || '',
  });

  const [changePassword, setChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [perfis, setPerfis] = useState<PerfilPermissao[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Carregar perfis disponíveis
  useEffect(() => {
    const fetchPerfis = async () => {
      try {
        const response = await apiService.getPerfisPermissao();
        setPerfis(response.data);
      } catch (error) {
        console.error('Erro ao carregar perfis:', error);
      }
    };

    fetchPerfis();
  }, []);

  // Validações
  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    // Validações obrigatórias
    if (!formData.nome?.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }

    if (!formData.email?.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Email deve ter um formato válido';
      }
    }

    // Validações de senha (apenas se estiver alterando)
    if (changePassword) {
      if (!newPassword) {
        newErrors.newPassword = 'Nova senha é obrigatória';
      } else if (newPassword.length < 6) {
        newErrors.newPassword = 'Nova senha deve ter no mínimo 6 caracteres';
      }

      if (!confirmPassword) {
        newErrors.confirmPassword = 'Confirmação de senha é obrigatória';
      } else if (newPassword !== confirmPassword) {
        newErrors.confirmPassword = 'Senhas não coincidem';
      }
    }

    // Validação de CPF (se fornecido)
    if (formData.cpf && formData.cpf.replace(/\D/g, '').length !== 11) {
      newErrors.cpf = 'CPF deve conter 11 dígitos';
    }

    // Validação de data de nascimento
    if (formData.data_nascimento) {
      const birthDate = new Date(formData.data_nascimento);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      
      if (age < 16 || age > 100) {
        newErrors.data_nascimento = 'Idade deve estar entre 16 e 100 anos';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Função para formatar CPF
  const formatCPF = (value: string | undefined) => {
    if (!value) return '';
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return value;
  };

  // Função para formatar telefone
  const formatPhone = (value: string | undefined) => {
    if (!value) return '';
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    return value;
  };

  const handleInputChange = (field: keyof UpdateUsuarioData, value: any) => {
    if (field === 'cpf') {
      value = formatCPF(value);
    } else if (field === 'telefone_celular') {
      value = formatPhone(value);
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Limpar erro do campo quando usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Preparar dados para envio (remover formatação)
      const dataToSend: UpdateUsuarioData = {
        ...formData,
        cpf: formData.cpf ? formData.cpf.replace(/\D/g, '') : '',
        telefone_celular: formData.telefone_celular ? formData.telefone_celular.replace(/\D/g, '') : '',
      };

      // Adicionar senha se estiver alterando
      if (changePassword && newPassword) {
        dataToSend.senha = newPassword;
      }

      await apiService.updateUsuario(usuario.id, dataToSend);
      
      // Sucesso
      alert('Usuário atualizado com sucesso!');
      onRefresh();
      onClose();
    } catch (error: any) {
      console.error('Erro ao atualizar usuário:', error);
      alert(error.message || 'Erro ao atualizar usuário. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Formatar valores iniciais (CPF e telefone)
  useEffect(() => {
    if (usuario.cpf) {
      setFormData(prev => ({
        ...prev,
        cpf: formatCPF(usuario.cpf)
      }));
    }
    if (usuario.telefone_celular) {
      setFormData(prev => ({
        ...prev,
        telefone_celular: formatPhone(usuario.telefone_celular)
      }));
    }
  }, [usuario]);

  return (
    <div
      className="modal fade show"
      style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
      tabIndex={-1}
      id="modal_editar_usuario"
    >
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="ti ti-user-edit me-2"></i>
              Editar Usuário: {usuario.nome}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={loading}
            />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="row">
                {/* Nome */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">
                      Nome <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${errors.nome ? 'is-invalid' : ''}`}
                      value={formData.nome || ''}
                      onChange={(e) => handleInputChange('nome', e.target.value)}
                      placeholder="Digite o nome"
                      disabled={loading}
                    />
                    {errors.nome && <div className="invalid-feedback">{errors.nome}</div>}
                  </div>
                </div>

                {/* Sobrenome */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Sobrenome</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.sobrenome || ''}
                      onChange={(e) => handleInputChange('sobrenome', e.target.value)}
                      placeholder="Digite o sobrenome"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">
                      Email <span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                      value={formData.email || ''}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="Digite o email"
                      disabled={loading}
                    />
                    {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                  </div>
                </div>

                {/* Cargo */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Cargo</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.cargo || ''}
                      onChange={(e) => handleInputChange('cargo', e.target.value)}
                      placeholder="Digite o cargo"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* CPF */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">CPF</label>
                    <input
                      type="text"
                      className={`form-control ${errors.cpf ? 'is-invalid' : ''}`}
                      value={formData.cpf || ''}
                      onChange={(e) => handleInputChange('cpf', e.target.value)}
                      placeholder="000.000.000-00"
                      maxLength={14}
                      disabled={loading}
                    />
                    {errors.cpf && <div className="invalid-feedback">{errors.cpf}</div>}
                  </div>
                </div>

                {/* RG */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">RG</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.rg || ''}
                      onChange={(e) => handleInputChange('rg', e.target.value)}
                      placeholder="Digite o RG"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Telefone */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Telefone</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.telefone_celular || ''}
                      onChange={(e) => handleInputChange('telefone_celular', e.target.value)}
                      placeholder="(00) 00000-0000"
                      maxLength={15}
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Data de Nascimento */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Data de Nascimento</label>
                    <input
                      type="date"
                      className={`form-control ${errors.data_nascimento ? 'is-invalid' : ''}`}
                      value={formData.data_nascimento || ''}
                      onChange={(e) => handleInputChange('data_nascimento', e.target.value)}
                      disabled={loading}
                    />
                    {errors.data_nascimento && <div className="invalid-feedback">{errors.data_nascimento}</div>}
                  </div>
                </div>

                {/* Perfil */}
                <div className="col-md-12">
                  <div className="mb-3">
                    <label className="form-label">Perfil de Acesso</label>
                    <select
                      className="form-select"
                      value={formData.id_perfil_base || ''}
                      onChange={(e) => handleInputChange('id_perfil_base', e.target.value ? parseInt(e.target.value) : undefined)}
                      disabled={loading}
                    >
                      <option value="">Selecione um perfil (opcional)</option>
                      {perfis.map(perfil => (
                        <option key={perfil.id} value={perfil.id}>
                          {perfil.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Opção de alterar senha */}
                <div className="col-md-12">
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="changePassword"
                        checked={changePassword}
                        onChange={(e) => {
                          setChangePassword(e.target.checked);
                          if (!e.target.checked) {
                            setNewPassword('');
                            setConfirmPassword('');
                            setErrors(prev => ({
                              ...prev,
                              newPassword: '',
                              confirmPassword: ''
                            }));
                          }
                        }}
                        disabled={loading}
                      />
                      <label className="form-check-label" htmlFor="changePassword">
                        Alterar senha
                      </label>
                    </div>
                  </div>
                </div>

                {/* Campos de senha (apenas se alterar senha estiver marcado) */}
                {changePassword && (
                  <>
                    {/* Nova Senha */}
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Nova Senha <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <input
                            type={showPassword ? "text" : "password"}
                            className={`form-control ${errors.newPassword ? 'is-invalid' : ''}`}
                            value={newPassword}
                            onChange={(e) => {
                              setNewPassword(e.target.value);
                              if (errors.newPassword) {
                                setErrors(prev => ({ ...prev, newPassword: '' }));
                              }
                            }}
                            placeholder="Digite a nova senha"
                            disabled={loading}
                          />
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => setShowPassword(!showPassword)}
                            disabled={loading}
                          >
                            <i className={`ti ${showPassword ? 'ti-eye-off' : 'ti-eye'}`}></i>
                          </button>
                        </div>
                        {errors.newPassword && <div className="invalid-feedback">{errors.newPassword}</div>}
                        <small className="text-muted">Mínimo de 6 caracteres</small>
                      </div>
                    </div>

                    {/* Confirmar Nova Senha */}
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Confirmar Nova Senha <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                            value={confirmPassword}
                            onChange={(e) => {
                              setConfirmPassword(e.target.value);
                              if (errors.confirmPassword) {
                                setErrors(prev => ({ ...prev, confirmPassword: '' }));
                              }
                            }}
                            placeholder="Confirme a nova senha"
                            disabled={loading}
                          />
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            disabled={loading}
                          >
                            <i className={`ti ${showConfirmPassword ? 'ti-eye-off' : 'ti-eye'}`}></i>
                          </button>
                        </div>
                        {errors.confirmPassword && <div className="invalid-feedback">{errors.confirmPassword}</div>}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-light"
                onClick={onClose}
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Salvando...
                  </>
                ) : (
                  <>
                    <i className="ti ti-device-floppy me-2"></i>
                    Salvar Alterações
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};